import { useEffect, useRef, useState } from 'react'
import { messagesApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'

interface Message {
  id: number
  sender_id: number
  body: string
  created_at: string
  read_at: string | null
}

const POLL_INTERVAL_MS = 7000

export default function MessagesPage() {
  const { user } = useAuth()
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const bottomRef = useRef<HTMLDivElement>(null)

  const sellerId = user ? Number(user.user_id) : null

  const fetchMessages = async () => {
    if (!sellerId) return
    try {
      const res = await messagesApi.list(sellerId)
      setMessages(res.data.messages)
    } catch {
      setError('No se pudieron cargar los mensajes')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchMessages()
    const interval = setInterval(fetchMessages, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerId])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!text.trim() || !sellerId) return
    setSending(true)
    setError('')
    try {
      await messagesApi.send(sellerId, text.trim())
      setText('')
      await fetchMessages()
    } catch {
      setError('No se pudo enviar el mensaje')
    } finally {
      setSending(false)
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-espresso-800 mb-6">Mensajes con el Administrador</h1>
      <div className="bg-cream-50 shadow rounded-lg flex flex-col h-[70vh]">
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="text-center text-espresso-500 py-8">Cargando...</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-espresso-500 py-8">
              Todavía no hay mensajes. Escribe el primero.
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.sender_id === sellerId
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-md px-4 py-2 rounded-lg text-sm ${
                      mine ? 'bg-terracotta-600 text-white' : 'bg-cream-100 text-espresso-800'
                    }`}
                  >
                    <div>{m.body}</div>
                    <div className={`text-xs mt-1 ${mine ? 'text-terracotta-100' : 'text-espresso-500'}`}>
                      {new Date(m.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>
              )
            })
          )}
          <div ref={bottomRef} />
        </div>
        {error && <div className="px-4 py-2 text-sm text-red-600 border-t border-espresso-100">{error}</div>}
        <form onSubmit={handleSend} className="border-t border-espresso-200 p-4 flex gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Escribe un mensaje..."
            maxLength={2000}
            className="flex-1 border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
          />
          <button
            type="submit"
            disabled={sending || !text.trim()}
            className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
          >
            Enviar
          </button>
        </form>
      </div>
    </div>
  )
}
